// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// ⚠️⚠️ «VOLTAR ATRÁS NO MAPA» — o botão e o Ctrl+Z (fatia 1)
// ============================================================================
//
// 📗 Cliente (ponto D): *«Tem de permitir voltar atrás em algo que se faça — isto é, **no mapa**
//    ter algo tipo voltar atrás como tem no Word.»*
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ PORQUE É QUE VIVE NA BARRA DE TOPO E NÃO NUMA JANELA
// ---------------------------------------------------------------------------
//
//   📗 Ele disse *«no mapa»*, e a medição diz porquê: `apps/web/src/mapa` tem **28 componentes de
//   janela**, **25 dos quais fazem pelo menos um `POST`**. Pôr o desfazer dentro de uma delas
//   obrigava a saber **em qual** se tinha errado — que é precisamente o que quem erra não sabe.
//
//   📌 Fica ao lado do «Hoje» e da lupa, no grupo dos utilitários: são os gestos de «como chego
//   lá / como volto atrás», e não sítios onde se trabalha.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ O BOTÃO FICA DESACTIVADO COM UMA RAZÃO ESCRITA, NUNCA INVISÍVEL
// ---------------------------------------------------------------------------
//
//   «Nada para desfazer», «o último gesto foi uma paragem e o registo não guarda o que lá
//   estava» e «esses dias já passaram» são **três estados diferentes**, e um botão que desaparece
//   diz exactamente a mesma coisa aos três — que a funcionalidade não existe.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ E CONFIRMA ANTES DE ESCREVER — não é cerimónia
// ---------------------------------------------------------------------------
//
//   📗 §5.1 do desenho: *«O desfazer tem de dizer isso ANTES de o utilizador clicar, não
//   depois»*. No Word o `Ctrl+Z` não pergunta nada porque o que ele desfaz é uma letra; aqui
//   desfaz-se uma **escrita no plano de uma empresa**, medida em 2 a 6 linhas de `alocacao` por
//   gesto. 📌 E há coisas que só se sabem do lado do servidor — quantos daqueles dias **já
//   passaram** (67,8 % das linhas com serviço do `dev`) e se o travão é forçável. A frase que
//   aparece antes do clique é essa medição, e não uma repetição do que o ecrã já mostrava.
//
// ⚠️ **A classificação NÃO vive aqui.** Que gestos se invertem, e com que frase se recusam os
//   outros, está em `apps/api/src/desfazer.ts` — um sítio só. Uma segunda tabela no navegador era
//   a formulação que diverge, e *«a que divergir é a que ninguém corre»*.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ REFAZER (2026-09-28) — o par do Ctrl+Z, no MESMO ciclo
// ---------------------------------------------------------------------------
//
//   📗 §5.4: «o refazer sai de graça». Espelha o Ctrl+Z linha a linha: a mesma pré-verificação
//   antes de escrever, o mesmo cartão com motivo quando os travões o exigem (D-007), a mesma
//   releitura por `sinal` e o mesmo recarregar do mapa. Não há caixa de motivo nova nem segundo
//   ciclo de conflito — é o MESMO, sobre `/api/plano/refazer` em vez de `/api/plano/desfazer`.
//
//   ⚠️⚠️ **SÓ `Ctrl+Y`, e NÃO `Ctrl+Shift+Z`** — medido antes de escolher, e a medição mudou a
//   decisão. `Ctrl+Shift+Z` é o convencional para refazer, mas JÁ TEM DONO aqui: o atalho do
//   Ctrl+Z lê `e.key.toLowerCase()` sem excluir `Shift` (é a prova
//   `Desfazer.test.tsx` · «e responde com Shift premido — a tecla é Z e o atalho tem de
//   continuar a valer», escrita de propósito), logo `Ctrl+Shift+Z` já ABRE O DESFAZER hoje.
//   *«Não roubes nenhum»* — `Ctrl+Y` é o único dos dois que está livre (as setas, `Shift` sozinho,
//   `Home`/`End`, `PageUp`/`PageDown`, `Enter` e `Esc` do mapa não levam `Ctrl`/`Cmd`, e o único
//   atalho global é `Ctrl+K`), e é o único que se usa.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ O REFAZER APARECE ONDE FAZ SENTIDO — no cartão de «Desfeito» (2026-09-29)
// ---------------------------------------------------------------------------
//
//   📗 Cliente, entre quatro formas propostas, escolheu a 1: *«Refazer só aparece logo depois de
//   desfazer»*. Até aqui era um botão só-ícone permanente na barra — o texto completo esbatia o
//   título da marca a 1600 px (`nada-e-cortado-pela-janela.test.tsx`), e um ícone espelhado sem
//   texto lia-se mal. 📌 Refazer só tem sentido IMEDIATAMENTE a seguir a um desfazer, e é aí que
//   ele está agora: no cartão que diz **o que foi desfeito**, com o texto «Refazer» e o `Ctrl+Y`
//   ao lado. A barra perde um botão em vez de ganhar texto.
//
//   ⚠️ **Não é um atalho que escreve.** O botão do cartão abre o MESMO cartão de confirmação do
//   `Ctrl+Y` (pré-verificação, travões, motivo — D-007); não há segundo caminho de escrita.
//   ⚠️ **E o `Ctrl+Y` continua a valer a qualquer momento** — fechado o cartão, é por ele que se
//   refaz, e o `title` do Desfazer di-lo para quem não o sabe.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { IconeDesfazer, IconeLivro } from './icones.js';
import { oFocoEEscrita } from './navegar-no-mapa.js';
// ⚠️ O DIÁRIO (peça 2 de «D») abre daqui **sem alvo** — é a vista geral, «tudo o que aconteceu
//    no plano». 📌 A exigência 3 do §5.6 («abre-se da célula do mapa», e filtrado por AQUELA
//    viagem) está ligada desde 2026-09-15 no `Mapa.tsx`, pelo MESMO componente com `alvo`.
import { Diario } from './Diario.js';
import { useEscape } from './janela.js';
// ⚠️⚠️ A leitura da recusa é a partilhada — ver a nota junto a `exigeMotivo` (2026-09-29).
import { recusaDoCorpo, useTravao, type CorpoDeRecusa } from './travao-no-ecra.js';
import { fraseDaRecusa, fraseDoPassoFalhado } from './frase-da-recusa.js';

/** O que o servidor diz sobre o topo da pilha. Espelha `TopoDaPilha` em `apps/api/src/desfazer.ts`. */
export interface TopoDaPilha {
  readonly gesto: {
    readonly em: string;
    readonly quando: string;
    readonly descricao: string;
    readonly accoes: readonly string[];
    readonly registos: number;
  } | null;
  readonly revertivel: boolean;
  readonly porque: string;
  /** 🔴 2026-10-26 · O que vai mudar, linha a linha (só nos gestos com captura, migração 100). */
  readonly alteracoes?: {
    readonly total: number;
    readonly preservadas: number;
    readonly linhas: readonly {
      readonly tabela: string;
      readonly linha: string;
      readonly tipo: 'volta' | 'sai' | 'muda';
      readonly colunas: readonly { readonly coluna: string; readonly agora: unknown; readonly fica: unknown }[];
    }[];
  };
  readonly travados: {
    readonly dias: number;
    readonly frase: string;
    readonly impedem: boolean;
  } | null;
}

const valorCurto = (x: unknown): string =>
  x === null || x === undefined || x === '' ? '—' : String(x).slice(0, 40);

/**
 * 🔴 2026-10-26 · 📗 «facilidade em auditar e verificar o que foi ou será feito». O que o Ctrl+Z
 * (ou o refazer) vai mudar, linha a linha, ANTES de se carregar — a mesma conta que a escrita faz.
 */
export function OQueVaiMudar({ alteracoes }: { readonly alteracoes: TopoDaPilha['alteracoes'] }) {
  if (alteracoes === undefined || alteracoes.total === 0) return null;
  const MOSTRAR = 12;
  return (
    <details className="desfazer-alteracoes">
      <summary>
        O que vai mudar ({alteracoes.total} linha(s)
        {alteracoes.preservadas > 0 ? `; ${String(alteracoes.preservadas)} do passado ficam como estão` : ''})
      </summary>
      <ul>
        {alteracoes.linhas.slice(0, MOSTRAR).map((l, i) => (
          <li key={`${l.tabela}-${l.linha}-${String(i)}`}>
            <strong>{l.linha}</strong>{' '}
            {l.tipo === 'volta' ? '— volta' : l.tipo === 'sai' ? '— sai' : null}
            {l.colunas.map((c) => (
              <span key={c.coluna} className="desfazer-coluna">
                {' · '}
                {c.coluna}: {valorCurto(c.agora)} → {valorCurto(c.fica)}
              </span>
            ))}
          </li>
        ))}
      </ul>
      {alteracoes.total > MOSTRAR ? <p>… e mais {alteracoes.total - MOSTRAR} linha(s).</p> : null}
    </details>
  );
}

/**
 * ⚠️ **A forma verifica-se; um `as` não a verifica.** Um 403, um 503 ou um portal de rede que
 * devolva HTML com 200 produzem um corpo de outra forma, e sem esta guarda o botão lia
 * `undefined` como «não há nada para desfazer» — que é indistinguível de um sistema a funcionar.
 */
function eTopo(x: unknown): x is TopoDaPilha {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return typeof o['revertivel'] === 'boolean' && typeof o['porque'] === 'string';
}

/**
 * O topo de uma das duas pilhas a partir da resposta do `GET`, com a guarda da forma. ⚠️ UMA
 * leitura para as duas releituras por `sinal` e para a do refazer logo a seguir a um desfazer —
 * três cópias divergiam na guarda. 📌 Recebe a resposta e não faz o `fetch`: cada chamador põe o
 * `.catch()` na sua cadeia, que é onde o D21 (`todo-gesto-de-rede-fala.test.ts`) o procura.
 */
async function topoDaResposta(r: Response): Promise<TopoDaPilha> {
  if (!r.ok) throw new Error(String(r.status));
  const corpo: unknown = await r.json();
  if (!eTopo(corpo)) throw new Error('forma inesperada');
  return corpo;
}

/**
 * 2026-10-27 · O GESTO FIXA-SE AO ABRIR O CARTÃO. A pilha relê-se a cada recarga do mapa
 * (`sinal`), logo com o cartão aberto outra janela podia gravar e o topo passar a ser OUTRO
 * gesto: a frase mudava em silêncio e o botão revertia esse outro — com o motivo escrito para o
 * primeiro. Fixa-se o `em` do topo que estava à vista quando o cartão abriu; se o topo mudar,
 * `mudou` diz-o e o botão de escrever espera por uma nova confirmação (`aceitar`).
 */
function useGestoFixado(
  aberto: boolean,
  topo: TopoDaPilha | null,
): { readonly mudou: boolean; readonly aceitar: () => void } {
  const [fixado, definirFixado] = useState<string | null | undefined>(undefined);
  const actual = topo?.gesto?.em ?? null;
  useEffect(() => {
    if (!aberto) definirFixado(undefined);
    else if (fixado === undefined && topo !== null) definirFixado(actual);
  }, [aberto, topo, fixado, actual]);
  return {
    mudou: aberto && topo !== null && fixado !== undefined && actual !== fixado,
    aceitar: () => definirFixado(actual),
  };
}

/** O relatório da inversão. */
interface GestoDesfeito {
  readonly descricao: string;
  readonly revertidos: number;
  readonly falhados: number;
  readonly diasPreservados: number;
  readonly resultados: readonly {
    readonly acao: string;
    readonly aplicado: boolean;
    readonly dias: number;
    readonly erro?: string;
    readonly detalhe?: string;
  }[];
}

export function Desfazer({
  sinal,
  recarregar,
}: {
  /**
   * ⚠️ **Não é dado: é o gatilho de releitura.** Muda a cada recarga do mapa, logo a pilha volta
   * a ser lida depois de qualquer escrita feita em qualquer janela. Sem ele, o botão continuava a
   * oferecer um gesto que já não é o topo — e a rota recusava-o com `nao_e_o_topo` **depois** do
   * clique, que é a família «uma recusa que não se vê».
   */
  readonly sinal: unknown;
  readonly recarregar?: (() => void) | undefined;
}): React.JSX.Element {
  const [topo, definirTopo] = useState<TopoDaPilha | null>(null);
  const [aberto, definirAberto] = useState(false);
  const [aEscrever, definirAEscrever] = useState(false);
  const [motivo, definirMotivo] = useState('');
  const [feito, definirFeito] = useState<GestoDesfeito | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [diarioAberto, definirDiarioAberto] = useState(false);

  // ⚠️⚠️ O par do Ctrl+Z (ver o cabeçalho) — MESMA forma (`TopoDaPilha`/`GestoDesfeito`), MESMO
  //    ciclo, rota diferente.
  const [topoRefazer, definirTopoRefazer] = useState<TopoDaPilha | null>(null);
  const [abertoRefazer, definirAbertoRefazer] = useState(false);
  // 📗 2026-11-03 · `Esc` fecha os dois cartões («Voltar atrás» e «Refazer») sem escrever nada: é só
  //    estado do ecrã, a mesma saída dos outros painéis da barra (`esc-fecha-o-que-esta-aberto.test.tsx`).
  useEscape(() => definirAberto(false), aberto);
  useEscape(() => definirAbertoRefazer(false), abertoRefazer);
  const [aEscreverRefazer, definirAEscreverRefazer] = useState(false);
  const [motivoRefazer, definirMotivoRefazer] = useState('');
  const [feitoRefazer, definirFeitoRefazer] = useState<GestoDesfeito | null>(null);
  const [erroRefazer, definirErroRefazer] = useState<string | null>(null);
  /**
   * ⚠️⚠️ **O topo do refazer RELIDO logo a seguir ao desfazer** — é ele que decide o «Refazer» do
   * cartão de «Desfeito». 📌 `revertidos > 0` não chegava: o inverso de um `dividir_viagem` escreve
   * `juntar_viagem`, e o de outros gestos um registo `sem-inverso` — o topo do refazer passa a ser
   * esse, e o botão levava a um cartão que dizia «Não se refaz». Relê-se aqui, sem esperar pelo
   * recarregar do mapa. `null` = a leitura falhou; `undefined` = ainda não se releu.
   */
  const [refazerAposDesfazer, definirRefazerAposDesfazer] = useState<TopoDaPilha | null | undefined>(
    undefined,
  );

  // 🔴 2026-10-30 · **O CARTÃO RELÊ O TOPO AO ABRIR** (defeito 4 das demonstrações). A pilha só
  //    se relia quando o mapa recarregava (`sinal`); um gesto gravado sem recarga — a troca que
  //    ficou feita quando o condutor recusou — deixava o cartão a mostrar o gesto ANTERIOR, e
  //    confirmar desfazia esse. Abrir conta como releitura (`releitura`), e o topo passa a
  //    `null` até chegar o novo, para o `useGestoFixado` fixar o lido e não o velho.
  const [releitura, definirReleitura] = useState(0);
  const [releituraRefazer, definirReleituraRefazer] = useState(0);

  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/plano/desfazer', { signal: abortar.signal })
      .then(topoDaResposta)
      .then((corpo) => definirTopo(corpo))
      .catch((e: unknown) => {
        // ⚠️ Um pedido abortado (o mapa recarregou entretanto) não é uma falha — marcá-lo como tal
        //    fazia o botão dizer «indisponível» a cada recarga.
        if (e instanceof DOMException && e.name === 'AbortError') return;
        definirTopo(null);
      });
    return () => abortar.abort();
  }, [sinal, releitura]);

  // ⚠️ MESMA releitura, MESMA guarda de forma (`eTopo`) — só a rota muda. Corre a par da de
  //    cima e não depois dela: o refazer tem de reagir ao MESMO `sinal`, não a um atraso dele.
  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/plano/refazer', { signal: abortar.signal })
      .then(topoDaResposta)
      .then((corpo) => definirTopoRefazer(corpo))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        definirTopoRefazer(null);
      });
    return () => abortar.abort();
  }, [sinal, releituraRefazer]);

  const abrir = useCallback(() => {
    definirTopo(null);
    definirReleitura((n) => n + 1);
    definirFeito(null);
    definirRefazerAposDesfazer(undefined);
    definirErro(null);
    definirMotivo('');
    definirAberto(true);
  }, []);

  const abrirRefazer = useCallback(() => {
    definirTopoRefazer(null);
    definirReleituraRefazer((n) => n + 1);
    definirFeitoRefazer(null);
    definirErroRefazer(null);
    definirMotivoRefazer('');
    definirAbertoRefazer(true);
  }, []);

  /**
   * ⚠️⚠️ **`Ctrl/Cmd+Z` para desfazer, `Ctrl/Cmd+Y` para refazer** — e não `Ctrl+Shift+Z`: ver o
   * cabeçalho («já tem dono» — o `Ctrl+Z` de baixo responde a `Shift+Z` de propósito, com prova
   * própria em `Desfazer.test.tsx`). `Ctrl+Y` é o único dos dois convencionais que está livre.
   * 📌 E é `ctrl`/`meta` e não uma tecla solta — uma tecla sem modificador roubava a escrita a
   * todos os campos de texto do ecrã, que é a razão escrita no `Ctrl+K`.
   *
   * ⚠️ **`e.key.toLowerCase()`**, senão com `Shift` premido a tecla é `Z` e o atalho do Ctrl+Z
   * deixava de responder — a mesma armadilha que o `Ctrl+K` já registava, e a razão pela qual
   * `Ctrl+Shift+Z` já cai no Desfazer em vez de ficar livre para o Refazer.
   */
  useEffect(() => {
    const ouvir = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey)) return;
      // 2026-10-27 · num campo de escrita o Ctrl+Z é do campo (apagar o que se escreveu), não do
      //    plano: antes o `preventDefault()` corria sem olhar para o alvo. Ver `oFocoEEscrita`.
      if (oFocoEEscrita(e.target)) return;
      const tecla = e.key.toLowerCase();
      if (tecla === 'z') {
        e.preventDefault();
        abrir();
      } else if (tecla === 'y') {
        e.preventDefault();
        abrirRefazer();
      }
    };
    window.addEventListener('keydown', ouvir);
    return () => window.removeEventListener('keydown', ouvir);
  }, [abrir, abrirRefazer]);

  const gesto = topo?.gesto ?? null;
  const podeDesfazer = topo?.revertivel === true && gesto !== null;
  // 2026-10-27 · o gesto que estava à vista quando o cartão abriu — ver `useGestoFixado`. Só
  //    conta enquanto não houver relatório: depois de desfazer, o topo muda por causa do próprio
  //    gesto, e isso não é «gravou-se noutra janela».
  const fixadoDesfazer = useGestoFixado(aberto && feito === null, topo);
  // ⚠️⚠️ **A PRÉ-VERIFICAÇÃO NÃO VÊ TUDO O QUE PEDE MOTIVO (2026-09-29).** 📗 *«confirmados pede
  //    motivo, mas não existe caixa para indicar o motivo»*. O `topo.travados` diz os travões
  //    que o servidor mediu ANTES; mas a escrita pode recusar por mais razões que pedem motivo —
  //    a espera que uma rotação recriada impõe ao grupo novo (`motivo_obrigatorio`, que o
  //    desfazer de um «pôr em espera» passou a poder dar), ou um travão que nasceu entre a
  //    leitura e o clique. 📌 Aí o campo não aparecia e o botão repetia a mesma recusa. A
  //    resposta passa a ser lida pela regra partilhada (`recusaDoCorpo`), e o campo abre-se.
  const travaoDesfazer = useTravao();
  const travaoRefazer = useTravao();
  // ⚠️ O travão **forçável** exige motivo — é a metade «e motivo» da regra do cliente
  //    (`travoes.ts`): a aceitação sozinha não é prova de que alguém decidiu.
  const exigeMotivo =
    (topo?.travados != null && !topo.travados.impedem) || travaoDesfazer.forcavel;
  const razao =
    gesto === null
      ? (topo?.porque ?? 'a pilha ainda não foi lida')
      : podeDesfazer
        ? `Desfazer: ${gesto.descricao}`
        : `Não se desfaz: ${topo?.porque ?? ''}`;

  async function desfazer(): Promise<void> {
    if (gesto === null) return;
    definirAEscrever(true);
    definirErro(null);
    try {
      const r = await fetch('/api/plano/desfazer', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          em: gesto.em,
          aceitaTravoes: exigeMotivo,
          motivo: motivo.trim() === '' ? null : motivo.trim(),
        }),
      });
      const corpo = (await r.json()) as Record<string, unknown>;
      if (!r.ok) {
        // ⚙ Para quem programa: só a frase — o código do erro (`corpo['erro']`) segue para o travão
        //   (`recusaDoCorpo`, abaixo) e não para o ecrã. Ver `frase-da-recusa.ts`.
        definirErro(fraseDaRecusa(corpo));
        travaoDesfazer.registar(recusaDoCorpo(corpo as CorpoDeRecusa, r.status, 'desfazer'));
        return;
      }
      travaoDesfazer.limpar();
      // ⚠️ Relê ANTES de mostrar o cartão, para o botão nascer já certo (nunca aparecer e sumir).
      const relido = await fetch('/api/plano/refazer')
        .then(topoDaResposta)
        .catch(() => null);
      definirRefazerAposDesfazer(relido);
      if (relido !== null) definirTopoRefazer(relido);
      definirFeito(corpo as unknown as GestoDesfeito);
      // ⚠️⚠️ **Recarrega o mapa.** Uma escrita que não se vê é indistinguível de uma que não
      //    aconteceu — e é essa a queixa exacta que este projecto já registou no rearranjo.
      recarregar?.();
    } catch (e: unknown) {
      definirErro(e instanceof Error ? e.message : String(e));
    } finally {
      definirAEscrever(false);
    }
  }

  // ⚠️⚠️ O PAR DE `gesto`/`podeDesfazer`/`exigeMotivo`/`razao`, sobre o topo do REFAZER — a MESMA
  //    forma, calculada da MESMA maneira, sobre `topoRefazer` em vez de `topo`.
  const gestoRefazer = topoRefazer?.gesto ?? null;
  const podeRefazer = topoRefazer?.revertivel === true && gestoRefazer !== null;
  // 2026-10-27 · a mesma regra do cartão do desfazer — uma só formulação (`useGestoFixado`).
  const fixadoRefazer = useGestoFixado(abertoRefazer && feitoRefazer === null, topoRefazer);
  const exigeMotivoRefazer =
    (topoRefazer?.travados != null && !topoRefazer.travados.impedem) || travaoRefazer.forcavel;
  const razaoRefazer =
    gestoRefazer === null
      ? (topoRefazer?.porque ?? 'a pilha ainda não foi lida')
      : podeRefazer
        ? `Refazer: ${gestoRefazer.descricao}`
        : `Não se refaz: ${topoRefazer?.porque ?? ''}`;

  /** ⚠️⚠️ O MESMO `desfazer()` de cima, sobre `/api/plano/refazer` — nenhum ciclo novo. */
  async function refazer(): Promise<void> {
    if (gestoRefazer === null) return;
    definirAEscreverRefazer(true);
    definirErroRefazer(null);
    try {
      const r = await fetch('/api/plano/refazer', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          em: gestoRefazer.em,
          aceitaTravoes: exigeMotivoRefazer,
          motivo: motivoRefazer.trim() === '' ? null : motivoRefazer.trim(),
        }),
      });
      const corpo = (await r.json()) as Record<string, unknown>;
      if (!r.ok) {
        definirErroRefazer(fraseDaRecusa(corpo));
        travaoRefazer.registar(recusaDoCorpo(corpo as CorpoDeRecusa, r.status, 'refazer'));
        return;
      }
      travaoRefazer.limpar();
      definirFeitoRefazer(corpo as unknown as GestoDesfeito);
      recarregar?.();
    } catch (e: unknown) {
      definirErroRefazer(e instanceof Error ? e.message : String(e));
    } finally {
      definirAEscreverRefazer(false);
    }
  }

  return (
    <span className="desfazer">
      <button
        type="button"
        className="btn btn--peq btn--desfazer"
        onClick={abrir}
        // 📗 2026-10-29 · abaixo de 1 200 px o rótulo sai da vista (`.rotulo-cede--tarde`, `mapa.css`):
        //    o nome passa a viver no `title` e no `aria-label`. ⚠️ O `aria-label` NÃO é «Desfazer» à
        //    letra — esse é o nome do botão de confirmar do cartão, e as provas distinguem-nos.
        title={`${razao.startsWith('Desfazer') ? '' : 'Desfazer — '}${razao} · Ctrl+Z (Ctrl+Y refaz)`}
        aria-label="Desfazer (Ctrl+Z)"
      >
        <IconeDesfazer em={0.9} />
        <span className="rotulo-cede rotulo-cede--tarde">
          Desfazer
          <kbd>Ctrl+Z</kbd>
        </span>
      </button>

      {/* ⚠️ O DIÁRIO — peça 2 de «D» (§5.5). Daqui abre a vista GERAL; da célula do mapa abre a
          da viagem, pelo mesmo componente com `alvo`. Ver o cabeçalho de `Diario.tsx`. */}
      <button
        type="button"
        className="btn btn--peq"
        onClick={() => definirDiarioAberto(true)}
        title="Diário — tudo o que aconteceu"
        aria-label="Diário"
      >
        <IconeLivro em={0.9} />
        <span className="rotulo-cede rotulo-cede--tarde">Diário</span>
      </button>
      {/* ⚠️ `recarregarMapa` é a peça 4: desfazer uma linha do diário é uma escrita no plano, e
          uma escrita que não se vê é indistinguível de uma que não aconteceu. */}
      {diarioAberto ? (
        <Diario fechar={() => definirDiarioAberto(false)} recarregarMapa={recarregar} />
      ) : null}

      {aberto ? (
        <div className="desfazer-cartao painel-da-barra" role="dialog" aria-label="Voltar atrás">
          {/* 2026-10-27 · o RELATÓRIO vem antes de «não há gesto»: depois de desfazer o último
              gesto da pilha o mapa recarrega, o topo relido passa a `gesto: null` — e o cartão
              trocava «Desfeito: … · N falhado(s) · M dia(s) ficaram como estavam» por «nada para
              desfazer». Era a prova do que foi feito a desaparecer logo a seguir. */}
          {feito !== null ? (
            <>
              <p className="desfazer-frase">
                <strong>Desfeito:</strong> {feito.descricao}
              </p>
              <p className="desfazer-razao">
                {feito.revertidos} revertido(s) · {feito.falhados} falhado(s)
                {/* ⚠️⚠️ **Os dias que ficaram são NOMEADOS.** Reverter 2 de 4 em silêncio deixava
                    o planeador a acreditar que o gesto tinha sido apagado por inteiro — é a
                    metade (b) da prova P5, e é a que uma implementação apressada cala. */}
                {feito.diasPreservados > 0
                  ? ` · ${feito.diasPreservados} dia(s) ficaram como estavam (já passaram ou já foram fechados)`
                  : ''}
              </p>
              {/* ⚠️⚠️ **A frase de um resultado APLICADO também se lê** (2026-09-29). Juntar uma
                  viagem partida é aplicado e traz uma frase que importa — «as metades ficaram em
                  autocarros diferentes», «N dia(s) sem viatura também juntos». Mostrar só a
                  `detalhe` dos falhados calava-a: a API dizia-o e o ecrã não. */}
              {feito.resultados
                .filter((x) => x.aplicado && x.detalhe !== undefined && x.detalhe !== '')
                .map((x, i) => (
                  <p className="desfazer-razao" key={`a${String(i)}`}>
                    {x.detalhe}
                  </p>
                ))}
              {feito.resultados
                .filter((x) => !x.aplicado)
                .map((x, i) => (
                  <p className="desfazer-erro" key={i}>
                    {fraseDoPassoFalhado(x)}
                  </p>
                ))}
              <div className="desfazer-accoes faixa-quebra">
                <button type="button" className="btn btn--peq" onClick={() => definirAberto(false)}>
                  Fechar
                </button>
                {/* ⚠️⚠️ O REFAZER, aqui e não na barra — ver o cabeçalho. Só quando alguma coisa
                    foi mesmo revertida E o topo do refazer, RELIDO depois do desfazer, se refaz:
                    o inverso de alguns gestos (`juntar_viagem`, `libertar_dias`) não tem inverso,
                    e o botão mandava confirmar um gesto que não se refaz. Abre o cartão de
                    confirmação do `Ctrl+Y` — não escreve directamente. */}
                {feito.revertidos > 0 && refazerAposDesfazer?.revertivel === true ? (
                  <button
                    type="button"
                    className="btn btn--peq btn--desfazer"
                    onClick={() => {
                      definirAberto(false);
                      abrirRefazer();
                    }}
                    title="Refazer o que acabou de ser desfeito · Ctrl+Y"
                  >
                    <span style={{ display: 'inline-flex', transform: 'scaleX(-1)' }}>
                      <IconeDesfazer em={0.9} />
                    </span>
                    Refazer
                    <kbd>Ctrl+Y</kbd>
                  </button>
                ) : null}
              </div>
              {/* ⚠️ Sem o botão, a RAZÃO por extenso — um botão que some sem explicação lê-se como
                  avaria. */}
              {feito.revertidos > 0 && refazerAposDesfazer?.revertivel !== true ? (
                <p className="desfazer-razao">
                  {refazerAposDesfazer == null
                    ? 'Não foi possível ler o que se pode refazer agora — use Ctrl+Y para ver.'
                    : `Não se refaz: ${refazerAposDesfazer.porque}`}
                </p>
              ) : null}
            </>
          ) : gesto === null ? (
            <p className="desfazer-razao">{topo?.porque ?? 'a pilha ainda não foi lida.'}</p>
          ) : (
            <>
              {fixadoDesfazer.mudou ? (
                <p className="desfazer-aviso" role="alert">
                  ⚠️ O último gesto mudou enquanto este cartão estava aberto (gravou-se entretanto
                  noutra janela). O que se desfaz agora é o de baixo — confirme-o antes de avançar.{' '}
                  <button
                    type="button"
                    className="btn btn--peq"
                    onClick={() => {
                      definirMotivo('');
                      fixadoDesfazer.aceitar();
                    }}
                  >
                    É este
                  </button>
                </p>
              ) : null}
              <p className="desfazer-frase">
                <strong>Desfazer:</strong> {gesto.descricao}
              </p>
              <OQueVaiMudar alteracoes={topo?.alteracoes} />
              {!podeDesfazer ? <p className="desfazer-razao">{topo?.porque}</p> : null}
              {topo?.travados != null ? (
                <p className="desfazer-aviso">
                  ⚠️ {topo.travados.frase}.{' '}
                  {topo.travados.impedem
                    ? 'Os que já passaram ou já foram fechados não se desfazem — reverte-se o resto.'
                    : 'Avançar apesar disto exige motivo.'}
                </p>
              ) : null}
              {podeDesfazer && exigeMotivo ? (
                <label className="desfazer-motivo">
                  Motivo
                  <input
                    type="text"
                    value={motivo}
                    onChange={(e) => definirMotivo(e.target.value)}
                    placeholder="porque é que se volta atrás"
                  />
                </label>
              ) : null}
              {erro !== null ? <p className="desfazer-erro">{erro}</p> : null}
              <div className="desfazer-accoes faixa-quebra">
                <button type="button" className="btn btn--peq" onClick={() => definirAberto(false)}>
                  Cancelar
                </button>
                {podeDesfazer ? (
                  <button
                    type="button"
                    className="btn btn--peq btn--principal"
                    disabled={aEscrever || fixadoDesfazer.mudou || (exigeMotivo && motivo.trim() === '')}
                    onClick={() => void desfazer()}
                  >
                    {aEscrever ? 'A desfazer…' : 'Desfazer'}
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* ⚠️ O CARTÃO DO REFAZER — a MESMA estrutura do de cima, sobre `topoRefazer`/`refazer()`.
          Reaproveita as classes `desfazer-*`: são o cartão genérico de confirmação (posição,
          cor, espaçamento), não uma semântica de «desfazer» — a mesma razão por que não se cria
          um `refazer-cartao` paralelo. */}
      {abertoRefazer ? (
        <div className="desfazer-cartao painel-da-barra" role="dialog" aria-label="Refazer">
          {/* 2026-10-27 · o relatório antes de «não há gesto» — a mesma razão do cartão de cima,
              e aqui é o caso NORMAL: a pilha do refazer tem quase sempre um gesto só, e refazê-lo
              esvazia-a. */}
          {feitoRefazer !== null ? (
            <>
              <p className="desfazer-frase">
                <strong>Refeito:</strong> {feitoRefazer.descricao}
              </p>
              <p className="desfazer-razao">
                {feitoRefazer.revertidos} revertido(s) · {feitoRefazer.falhados} falhado(s)
                {feitoRefazer.diasPreservados > 0
                  ? ` · ${feitoRefazer.diasPreservados} dia(s) ficaram como estavam (já passaram ou já foram fechados)`
                  : ''}
              </p>
              {feitoRefazer.resultados
                .filter((x) => x.aplicado && x.detalhe !== undefined && x.detalhe !== '')
                .map((x, i) => (
                  <p className="desfazer-razao" key={`a${String(i)}`}>
                    {x.detalhe}
                  </p>
                ))}
              {feitoRefazer.resultados
                .filter((x) => !x.aplicado)
                .map((x, i) => (
                  <p className="desfazer-erro" key={i}>
                    {fraseDoPassoFalhado(x)}
                  </p>
                ))}
              <div className="desfazer-accoes faixa-quebra">
                <button
                  type="button"
                  className="btn btn--peq"
                  onClick={() => definirAbertoRefazer(false)}
                >
                  Fechar
                </button>
              </div>
            </>
          ) : gestoRefazer === null ? (
            <p className="desfazer-razao">{topoRefazer?.porque ?? 'a pilha ainda não foi lida.'}</p>
          ) : (
            <>
              {fixadoRefazer.mudou ? (
                <p className="desfazer-aviso" role="alert">
                  ⚠️ O gesto a refazer mudou enquanto este cartão estava aberto (gravou-se entretanto
                  noutra janela). Confirme o de baixo antes de avançar.{' '}
                  <button
                    type="button"
                    className="btn btn--peq"
                    onClick={() => {
                      definirMotivoRefazer('');
                      fixadoRefazer.aceitar();
                    }}
                  >
                    É este
                  </button>
                </p>
              ) : null}
              <p className="desfazer-frase">
                <strong>Refazer:</strong> {gestoRefazer.descricao}
              </p>
              <OQueVaiMudar alteracoes={topoRefazer?.alteracoes} />
              {!podeRefazer ? <p className="desfazer-razao">{topoRefazer?.porque}</p> : null}
              {topoRefazer?.travados != null ? (
                <p className="desfazer-aviso">
                  ⚠️ {topoRefazer.travados.frase}.{' '}
                  {topoRefazer.travados.impedem
                    ? 'Os que já passaram ou já foram fechados não se desfazem — reverte-se o resto.'
                    : 'Avançar apesar disto exige motivo.'}
                </p>
              ) : null}
              {podeRefazer && exigeMotivoRefazer ? (
                <label className="desfazer-motivo">
                  Motivo
                  <input
                    type="text"
                    value={motivoRefazer}
                    onChange={(e) => definirMotivoRefazer(e.target.value)}
                    placeholder="porque é que se refaz"
                  />
                </label>
              ) : null}
              {erroRefazer !== null ? <p className="desfazer-erro">{erroRefazer}</p> : null}
              <div className="desfazer-accoes faixa-quebra">
                <button
                  type="button"
                  className="btn btn--peq"
                  onClick={() => definirAbertoRefazer(false)}
                >
                  Cancelar
                </button>
                {podeRefazer ? (
                  <button
                    type="button"
                    className="btn btn--peq btn--principal"
                    disabled={
                      aEscreverRefazer ||
                      fixadoRefazer.mudou ||
                      (exigeMotivoRefazer && motivoRefazer.trim() === '')
                    }
                    onClick={() => void refazer()}
                  >
                    {aEscreverRefazer ? 'A refazer…' : 'Refazer'}
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}
    </span>
  );
}
